import React__default from 'react';
import { useController } from 'react-hook-form';
import { TextInput } from '../../../atoms/TextInput.js';
import { FormItem } from '../FormItem.js';
import { formatReadOnlyValue, ReadOnlyField } from './ReadOnlyFormField.js';

/**
 * Wrapper component that intercepts props from FormItem and converts
 * between decimal (stored) and percentage (display) for percentage fields
 */
const PercentageTextInput = React__default.forwardRef(({ value, onChange, ...props }, ref) => {
    // Convert stored decimal value to percentage display value
    const getDisplayValue = () => {
        if (value === undefined || value === null || value === '') {
            return '';
        }
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(numValue)) {
            return '';
        }
        // Round to avoid float display issues (e.g., 8.0000001)
        return parseFloat((numValue * 100).toFixed(10)).toString();
    };
    // Convert percentage input value to decimal for storage
    const handleChange = (inputValue, source) => {
        // Empty input -> store undefined (not '' which becomes 0)
        if (!inputValue || inputValue.trim() === '') {
            // Cast to any since adapter expects string, but RHF accepts any
            onChange?.(undefined, source);
            return;
        }
        const numValue = parseFloat(inputValue);
        if (isNaN(numValue)) {
            onChange?.(undefined, source);
            return;
        }
        // Store as number, rounded to avoid float precision issues
        const decimalValue = parseFloat((numValue / 100).toFixed(10));
        onChange?.(decimalValue, source);
    };
    return React__default.createElement(TextInput, { ...props, ref: ref, value: getDisplayValue(), onChange: handleChange });
});
PercentageTextInput.displayName = 'PercentageTextInput';
/**
 * TextFormField - A form field component for text input.
 *
 * @example
 * ```tsx
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <TextFormField
 *     schemaField="email"
 *     labelText="Email Address"
 *     type="email"
 *     placeholder="Enter your email"
 *   />
 * </Form>
 * ```
 */
function TextFormField({ schemaField, labelText, descriptionText, isReadOnly, isDisabled, required, orientation, className, id, size = 'medium', type = 'text', variant, linkedField, showFieldToggle, ...textInputProps }) {
    const { field } = useController({
        name: schemaField,
        shouldUnregister: false,
    });
    const isPercentage = type === 'percentage';
    if (isReadOnly) {
        let displayValue;
        if (isPercentage) {
            const numValue = typeof field.value === 'number'
                ? field.value
                : parseFloat(String(field.value ?? ''));
            if (field.value == null || field.value === '' || isNaN(numValue)) {
                displayValue = formatReadOnlyValue(field.value, 'text');
            }
            else {
                // Round and format with % symbol
                displayValue = `${parseFloat((numValue * 100).toFixed(10))}%`;
            }
        }
        else {
            displayValue = formatReadOnlyValue(field.value, 'text');
        }
        return (React__default.createElement(ReadOnlyField, { label: labelText, value: displayValue, description: descriptionText, required: required, className: className }));
    }
    // Use PercentageTextInput wrapper for percentage type, otherwise use regular TextInput
    const InputComponent = isPercentage ? PercentageTextInput : TextInput;
    return (React__default.createElement(FormItem, { schemaField: schemaField, labelText: labelText, descriptionText: descriptionText, required: required, className: className, id: id, isDisabled: isDisabled, orientation: orientation, linkedField: linkedField, showFieldToggle: showFieldToggle },
        React__default.createElement(InputComponent, { ...textInputProps, isDisabled: isDisabled, isReadOnly: isReadOnly, size: size, type: type, variant: variant })));
}

export { TextFormField };
//# sourceMappingURL=TextFormField.js.map
